// NavBarHome.js
import React, { useState, useEffect } from 'react';
import { HashLink } from 'react-router-hash-link';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightToBracket,
  faWandMagicSparkles,
  faBars,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

const NavBarHome = ({ language }) => {
  const [isHoveringDemo, setIsHoveringDemo] = useState(false);
  const [isHoveringLogin, setIsHoveringLogin] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false); // Estado para el menú móvil
  const [isMobile, setIsMobile] = useState(false); // Estado para detectar pantalla móvil

  // Traducciones locales del componente
  const translations = {
    es: {
      product: 'Producto',
      solutions: 'Soluciones',
      services: 'Servicios',
      resources: 'Recursos',
      pricing: 'Precios',
      login: 'Iniciar sesión',
      demo: 'Demo',
      menu: 'Menú',
    },
    en: {
      product: 'Product',
      solutions: 'Solutions',
      services: 'Services',
      resources: 'Resources',
      pricing: 'Pricing',
      login: 'Log In',
      demo: 'Demo',
      menu: 'Menu',
    },
  };

  // Seleccionar traducción según el idioma
  const t = translations[language] || translations['en'];

  // Función para manejar el redimensionamiento de la ventana
  const handleResize = () => {
    if (window.innerWidth <= 768) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
      setIsMenuOpen(false); // Cerrar el menú si se cambia a pantalla grande
    }
  };

  // Configurar el listener para el resize al montar el componente
  useEffect(() => {
    handleResize(); // Verificar al montar
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Función para cerrar el menú al hacer clic en un enlace
  const handleLinkClick = () => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };

  return (
    <>
      {/* Estilos Internos para Media Queries */}
      <style>
        {`
          /* Estilos Generales de la Barra de Navegación */
          .navbar {
            display: flex;
            align-items: center;
            padding: 10px 20px;
            background-color: #ffffff;
            border-bottom: 1px solid #ddd;
            position: sticky;
            top: 0;
            z-index: 1000;
            height: 60px;
            justify-content: space-between;
          }

          /* Contenedor del Logo */
          .logoContainer {
            text-decoration: none;
          }

          .logo {
            height: 25px;
          }

          /* Botón de Menú (Hamburguesa) */
          .menuButton {
            display: none; /* Oculto en pantallas grandes */
            background: none;
            border: none;
            cursor: pointer;
            font-size: 24px;
          }

          .menuIcon {
            color: #333;
          }

          /* Enlaces de Navegación */
          .navLinksContainer {
            display: flex;
            gap: 20px;
            align-items: center;
          }

          .navLink {
            text-decoration: none;
            color: #333;
            font-size: 16px;
            font-weight: 400;
            font-family: 'Helvetica Neue', Arial, sans-serif;
          }

          /* Botones de Navegación */
          .navButtons {
            display: flex;
            gap: 10px;
          }

          .navPrimaryButton {
            text-decoration: none;
            color: #555555;
            border: 2px solid #555555;
            background-color: transparent;
            padding: 8px 16px;
            border-radius: 4px;
            font-weight: 700;
            font-family: 'Helvetica Neue', Arial, sans-serif;
            display: flex;
            align-items: center;
            gap: 8px;
            transition: color 0.3s ease, border-color 0.3s ease, background-color 0.3s ease;
          }

          .navPrimaryButtonHover {
            color: #ff2300;
            border-color: #ff2300;
            background-color: transparent;
          }

          .icon {
            font-size: 16px;
          }

          /* Menú Desplegable en Móviles */
          .mobileMenu {
            display: none; /* Oculto por defecto */
            position: absolute;
            top: 60px;
            left: 0;
            right: 0;
            background-color: #ffffff;
            flex-direction: column;
            align-items: flex-start;
            padding: 10px 20px;
            border-bottom: 1px solid #ddd;
            gap: 10px;
          }

          .mobileMenuOpen {
            display: flex;
          }

          /* Botones dentro del Menú Móvil */
          .navButtonsMobile {
            display: flex;
            flex-direction: column;
            gap: 10px;
            width: 100%;
          }

          .navButtonsMobile .navPrimaryButton {
            width: 100%; /* Ocupan todo el ancho en móviles */
            justify-content: center;
          }

          /* Media Queries para Responsividad */
          @media (max-width: 768px) {
            /* Mostrar el botón de menú en pantallas pequeñas */
            .menuButton {
              display: block;
            }

            /* Ocultar los enlaces de navegación y los botones en pantallas pequeñas */
            .navLinksContainer,
            .navButtons {
              display: none;
            }

            /* Mostrar el menú desplegable cuando está abierto */
            .mobileMenu {
              display: none;
            }

            .mobileMenuOpen {
              display: flex;
            }
          }
        `}
      </style>

      <nav className="navbar">
        {/* Logo */}
        <HashLink to="/" className="logoContainer" onClick={handleLinkClick}>
          <img
            src="/BetherLogoLuminousOrange.png"
            alt="Bether Logo"
            className="logo"
          />
        </HashLink>

        {/* Botón de Menú para móviles */}
        {isMobile && (
          <button
            className="menuButton"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={t.menu}
          >
            <FontAwesomeIcon
              icon={isMenuOpen ? faTimes : faBars}
              className="menuIcon"
            />
          </button>
        )}

        {/* Enlaces de navegación y botones para pantallas grandes */}
        {!isMobile && (
          <>
            <div className="navLinksContainer">
              <HashLink smooth to="/#product" className="navLink">
                {t.product}
              </HashLink>
              <HashLink smooth to="/#solutions" className="navLink">
                {t.solutions}
              </HashLink>
              <HashLink smooth to="/#services" className="navLink">
                {t.services}
              </HashLink>
              <HashLink smooth to="/#pricing" className="navLink">
                {t.pricing}
              </HashLink>
            </div>

            <div className="navButtons">
              <HashLink
                to="/register"
                style={
                  isHoveringLogin
                    ? {
                        ...styles.navPrimaryButton,
                        ...styles.navPrimaryButtonHover,
                      }
                    : styles.navPrimaryButton
                }
                className={isHoveringLogin ? 'navPrimaryButtonHover' : ''}
                onMouseEnter={() => setIsHoveringLogin(true)}
                onMouseLeave={() => setIsHoveringLogin(false)}
              >
                <FontAwesomeIcon
                  icon={faArrowRightToBracket}
                  className="icon"
                />
                {t.login}
              </HashLink>
              <HashLink
                to="/demo"
                style={
                  isHoveringDemo
                    ? {
                        ...styles.navPrimaryButton,
                        ...styles.navPrimaryButtonHover,
                      }
                    : styles.navPrimaryButton
                }
                className={isHoveringDemo ? 'navPrimaryButtonHover' : ''}
                onMouseEnter={() => setIsHoveringDemo(true)}
                onMouseLeave={() => setIsHoveringDemo(false)}
              >
                <FontAwesomeIcon icon={faWandMagicSparkles} className="icon" />
                {t.demo}
              </HashLink>
            </div>
          </>
        )}

        {/* Menú Desplegable para móviles */}
        {isMobile && (
          <div className={`mobileMenu ${isMenuOpen ? 'mobileMenuOpen' : ''}`}>
            <HashLink
              smooth
              to="/#product"
              className="navLink"
              onClick={handleLinkClick}
            >
              {t.product}
            </HashLink>
            <HashLink
              smooth
              to="/#solutions"
              className="navLink"
              onClick={handleLinkClick}
            >
              {t.solutions}
            </HashLink>
            <HashLink
              smooth
              to="/#services"
              className="navLink"
              onClick={handleLinkClick}
            >
              {t.services}
            </HashLink>
            <HashLink
              smooth
              to="/#pricing"
              className="navLink"
              onClick={handleLinkClick}
            >
              {t.pricing}
            </HashLink>

            {/* Botones dentro del menú móvil */}
            <div className="navButtonsMobile">
              <HashLink
                to="/register"
                style={
                  isHoveringLogin
                    ? {
                        ...styles.navPrimaryButton,
                        ...styles.navPrimaryButtonHover,
                      }
                    : styles.navPrimaryButton
                }
                className={isHoveringLogin ? 'navPrimaryButtonHover' : ''}
                onMouseEnter={() => setIsHoveringLogin(true)}
                onMouseLeave={() => setIsHoveringLogin(false)}
                onClick={handleLinkClick}
              >
                <FontAwesomeIcon
                  icon={faArrowRightToBracket}
                  className="icon"
                />
                {t.login}
              </HashLink>
              <HashLink
                to="/demo"
                style={
                  isHoveringDemo
                    ? {
                        ...styles.navPrimaryButton,
                        ...styles.navPrimaryButtonHover,
                      }
                    : styles.navPrimaryButton
                }
                className={isHoveringDemo ? 'navPrimaryButtonHover' : ''}
                onMouseEnter={() => setIsHoveringDemo(true)}
                onMouseLeave={() => setIsHoveringDemo(false)}
                onClick={handleLinkClick}
              >
                <FontAwesomeIcon icon={faWandMagicSparkles} className="icon" />
                {t.demo}
              </HashLink>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

// Estilos en línea para botones que cambian de estilo al pasar el cursor
const styles = {
  navPrimaryButton: {
    textDecoration: 'none',
    color: '#555555',
    borderWidth: '2px',
    borderStyle: 'solid',
    borderColor: '#555555',
    backgroundColor: 'transparent',
    padding: '8px 16px',
    borderRadius: '4px',
    fontWeight: '700',
    fontFamily: "'Helvetica Neue', Arial, sans-serif",
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition:
      'color 0.3s ease, border-color 0.3s ease, background-color 0.3s ease',
  },
  navPrimaryButtonHover: {
    color: '#ff2300',
    borderColor: '#ff2300',
  },
};

export default NavBarHome;
